import React from 'react';
import styles from './campaignBuilder.module.css';

/**
 * Scoped, purely-presentational primitives for the Campaign Builder admin.
 * All visual styling comes from campaignBuilder.module.css, which is scoped
 * to `.builderRoot` and uses `--cb-*` prefixed custom properties so nothing
 * leaks into the global / MUI stylesheet. No data fetching happens here.
 */

export { styles as builderStyles };

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/** Wrapper that establishes the scoped token context. Everything else must
 *  render inside a BuilderRoot for the `--cb-*` variables to resolve.
 *  `wide` widens the reading-width cap for dashboard/reporting surfaces (the
 *  authoring forms keep the default narrow, readable column). */
export function BuilderRoot(props: {
  children: React.ReactNode;
  className?: string;
  wide?: boolean;
}): JSX.Element {
  return (
    <div className={cx(styles.builderRoot, props.wide && styles.builderRootWide, props.className)}>
      {props.children}
    </div>
  );
}

/** Elevated surface. Becomes interactive (pointer cursor) when onClick is set. */
export function Card(props: {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}): JSX.Element {
  return (
    <div
      className={cx(styles.card, props.className)}
      onClick={props.onClick}
      role={props.onClick ? 'button' : undefined}
      tabIndex={props.onClick ? 0 : undefined}
      style={props.onClick ? { cursor: 'pointer' } : undefined}
    >
      {props.children}
    </div>
  );
}

/** Titled content group with an optional accent icon tile and header actions. */
export function Block(props: {
  icon?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
}): JSX.Element {
  return (
    <div className={styles.block}>
      <div className={styles.blockHeader}>
        {props.icon != null && <div className={styles.blockIcon}>{props.icon}</div>}
        <h4 className={styles.blockTitle}>{props.title}</h4>
        {props.actions != null && (
          <div className={styles.sectionActions} style={{ marginLeft: 'auto' }}>
            {props.actions}
          </div>
        )}
      </div>
      {props.subtitle != null && <p className={styles.lead}>{props.subtitle}</p>}
      {props.children}
    </div>
  );
}

/** Page/section heading with optional subtitle and right-aligned actions.
 *  `compact` shrinks the title to a panel-level heading (for a heading that sits inside a
 *  workspace pane rather than at the top of a page). */
export function SectionHeader(props: {
  title: React.ReactNode;
  actions?: React.ReactNode;
  subtitle?: React.ReactNode;
  compact?: boolean;
}): JSX.Element {
  return (
    <div className={cx(styles.sectionHeader, props.compact && styles.sectionHeaderCompact)}>
      <div className={styles.sectionHeaderMain}>
        <h2 className={cx(styles.sectionTitle, props.compact && styles.sectionTitleCompact)}>
          {props.title}
        </h2>
        {props.subtitle != null && (
          <p className={cx(styles.sectionSubtitle, props.compact && styles.sectionSubtitleCompact)}>
            {props.subtitle}
          </p>
        )}
      </div>
      {props.actions != null && (
        <div className={styles.sectionActions}>{props.actions}</div>
      )}
    </div>
  );
}

export type StatusTone = 'done' | 'progress' | 'idle' | 'warn' | 'danger';

const TONE_CLASS: Record<StatusTone, string> = {
  done: styles.toneDone,
  progress: styles.toneProgress,
  idle: styles.toneIdle,
  warn: styles.toneWarn,
  danger: styles.toneDanger,
};

/** Small uppercase status pill, coloured by tone. */
export function StatusPill(props: {
  tone: StatusTone;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <span className={cx(styles.statusPill, TONE_CLASS[props.tone])}>
      {props.children}
    </span>
  );
}

/** Uppercase accent pill used for phase / category labels. */
export function Tag(props: { children: React.ReactNode }): JSX.Element {
  return <span className={styles.tag}>{props.children}</span>;
}

/** Filled accent button. Spreads all native button props. */
export function PrimaryButton(
  props: React.ButtonHTMLAttributes<HTMLButtonElement>,
): JSX.Element {
  const { className, type, children, ...rest } = props;
  return (
    <button
      type={type ?? 'button'}
      className={cx(styles.btn, styles.primary, className)}
      {...rest}
    >
      {children}
    </button>
  );
}

/** Low-emphasis transparent button. Spreads all native button props. */
export function GhostButton(
  props: React.ButtonHTMLAttributes<HTMLButtonElement>,
): JSX.Element {
  const { className, type, children, ...rest } = props;
  return (
    <button
      type={type ?? 'button'}
      className={cx(styles.btn, styles.ghost, className)}
      {...rest}
    >
      {children}
    </button>
  );
}

/** Thin determinate progress bar. `value` is clamped to 0-100. */
export function ProgressBar(props: { value: number }): JSX.Element {
  const pct = Math.max(0, Math.min(100, props.value));
  return (
    <div
      className={styles.progressTrack}
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className={styles.progressFill} style={{ width: `${pct}%` }} />
    </div>
  );
}
